import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { FlowEditorApplyOp } from 'src/constants/actionTypes'
import { isFlowEditorAddStepTypeDisabled } from 'src/constants/flowEditorFeatureFlags'
import { getFlowEditorDraft } from 'src/selectors/onboardingFlowEditor'
import { createStepDrafts } from 'src/utils/flowEditorDocument/draftFactory'
import { logger } from 'src/utils/logger'

interface UseFlowEditorAddStepsParams {
  setSelectedGroupConfigId: Dispatch<SetStateAction<string | null>>
  setSelectedStepId: Dispatch<SetStateAction<string | null>>
  templateId: string
}

export function useFlowEditorAddSteps({
  setSelectedGroupConfigId,
  setSelectedStepId,
  templateId,
}: UseFlowEditorAddStepsParams) {
  const dispatch = useDispatch()
  const draft = useSelector(getFlowEditorDraft)
  const [showAddModal, setShowAddModal] = useState(false)
  const [pendingAddStepsConfigId, setPendingAddStepsConfigId] = useState<string | null>(null)

  const addStepsToConfig = useCallback(
    (types: CollectDataStepType[], targetCollectConfigId: string) => {
      const allowedTypes = types.filter((type) => {
        return !isFlowEditorAddStepTypeDisabled(type)
      })

      if (allowedTypes.length === 0) {
        if (types.length > 0) {
          logger.warn('Add step ignored: requested type(s) are disabled in feature flags', {
            templateId,
          })
        }

        return
      }

      if (allowedTypes.length < types.length) {
        logger.warn('Some add-step types were skipped (disabled in feature flags)', {
          templateId,
          requested: types,
          added: allowedTypes,
        })
      }

      if (!draft) {
        return
      }

      const existingSteps = Object.values(draft.stepsByLocalId)
      const newSteps = createStepDrafts(allowedTypes, targetCollectConfigId, existingSteps)

      if (newSteps.length === 0) {
        return
      }

      dispatch(
        FlowEditorApplyOp({
          kind: 'addSteps',
          configLocalId: targetCollectConfigId,
          steps: newSteps,
        }),
      )

      const stepIdToSelect = newSteps[newSteps.length - 1]?.localId ?? null

      if (stepIdToSelect !== null) {
        setSelectedGroupConfigId(null)
        setSelectedStepId(stepIdToSelect)
      }
    },
    [dispatch, draft, setSelectedGroupConfigId, setSelectedStepId, templateId],
  )

  const openAddStepModal = useCallback((configId: string) => {
    setPendingAddStepsConfigId(configId)
    setShowAddModal(true)
  }, [])

  const closeAddStepModal = useCallback(() => {
    setShowAddModal(false)
    setPendingAddStepsConfigId(null)
  }, [])

  const addStepsToGroup = useCallback(
    (types: CollectDataStepType[]) => {
      if (!pendingAddStepsConfigId) {
        return
      }

      addStepsToConfig(types, pendingAddStepsConfigId)
      setPendingAddStepsConfigId(null)
      setShowAddModal(false)
    },
    [addStepsToConfig, pendingAddStepsConfigId],
  )

  return {
    addStepsToGroup,
    closeAddStepModal,
    openAddStepModal,
    showAddModal,
  }
}
